import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { Loader2 } from 'lucide-react';

const VerifyEmail = () => {
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  const { verify, resendVerification } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const email = location.state?.email;

  useEffect(() => {
    if (!email) {
      navigate('/login');
    }
  }, [email, navigate]);

  useEffect(() => {
    let timer;
    if (cooldown > 0) {
      timer = setInterval(() => {
        setCooldown((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [cooldown]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');

    if (code.length !== 6) {
      setError('Please enter a 6-digit code.');
      return;
    }

    setLoading(true);
    try {
      await verify(email, code);
      navigate('/');
    } catch (err) {
      setError(err.response?.data?.msg || 'Verification failed');
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setError('');
    setMessage('');
    setResending(true);
    try {
      const res = await resendVerification(email);
      setMessage(res.msg || 'Verification code resent!');
      setCooldown(120); // 2 minute cooldown
    } catch (err) {
      setError(err.response?.data?.msg || 'Failed to resend code');
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="max-w-md mx-auto mt-20 card">
      <h2 className="text-3xl font-bold text-center text-xmas-red mb-6">Verify Your Email</h2>
      <p className="text-center mb-6 text-gray-600">
        We've sent a 6-digit verification code to <span className="font-semibold text-gray-800">{email}</span>.
        Please enter it below to activate your account.
      </p>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-center mb-2">Verification Code</label>
          <input
            type="text"
            maxLength="6"
            className="w-full p-4 border rounded text-center text-2xl tracking-widest font-bold focus:ring-2 focus:ring-xmas-green focus:border-transparent outline-none"
            placeholder="000000"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
            required
          />
        </div>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-2 rounded relative text-sm">
            {error}
          </div>
        )}

        {message && (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-2 rounded relative text-sm">
            {message}
          </div>
        )}

        <button
          type="submit"
          className="btn-primary w-full py-3 text-lg disabled:opacity-50"
          disabled={loading || code.length !== 6}
        >
          {loading ? (
            <>
              <Loader2 className="animate-spin" size={24} />
              Verifying...
            </>
          ) : (
            'Verify Account'
          )}
        </button>
      </form>

      <div className="mt-8 text-center">
        <p className="text-sm text-gray-500 mb-2">Didn't receive the code?</p>
        <button
          onClick={handleResend}
          disabled={resending || cooldown > 0}
          className="text-xmas-green font-bold hover:underline disabled:opacity-50 disabled:no-underline"
        >
          {resending ? (
            <span className="flex items-center justify-center">
              <Loader2 className="animate-spin mr-2" size={16} />
              Resending...
            </span>
          ) : cooldown > 0 ? (
            `Resend code in ${Math.floor(cooldown / 60)}:${(cooldown % 60).toString().padStart(2, '0')}`
          ) : (
            'Resend Verification Code'
          )}
        </button>
      </div>
    </div>
  );
};

export default VerifyEmail;
